import { useMemo } from "react";
import { instantMeiliSearch } from "@meilisearch/instant-meilisearch";
import {
  InstantSearch,
  SearchBox,
  Hits,
  Pagination,
  Stats,
  Configure,
  Highlight,
  SortBy,
  useRefinementList,
} from "react-instantsearch";
import "instantsearch.css/themes/satellite-min.css";
import { InstantSearchInputIcon, InstantSearchResultsLoading } from "@/components/SearchLoading";
import NewBadge from "@/components/NewBadge";
import { Lock } from "lucide-react";
import { alientechVehicleTypeLabel, alientechFuelLabel, ecuTypeLabel } from "@/lib/labels";
import { useBrandLogos, brandLogoFor } from "@/lib/brandLogos";
import MethodGlyph from "@/components/MethodGlyph";
import EcuBrandLogo, { ecuBrandLogoAvailable } from "@/components/EcuBrandLogo";
import VehicleTypeIcon from "@/components/VehicleTypeIcon";

const MODE_METHODS = {
  OBD: "obd",
  OBD_FULL: "obd",
  BENCH: "bench",
  BOOT: "boot",
  BOOT_GLITCH: "boot",
};

/**
 * Meilisearch InstantSearch UI for the `toolscraper_magicmotorsport` index —
 * sibling of CompatibilitySearch.jsx / AlientechInstantSearch.jsx, kept as
 * its own component since the schema (working_modes dict, ecu_maker,
 * produced_from_year/to_year) differs from both other providers.
 */
export default function MagicMotorsportInstantSearch({
  meiliHost,
  meiliSearchKey = "unused",
  indexName = "toolscraper_magicmotorsport",
  hitsPerPage = 20,
  className = "",
}) {
  if (!meiliHost) {
    throw new Error("MagicMotorsportInstantSearch: meiliHost prop is required");
  }

  const brandLogos = useBrandLogos();

  const searchClient = useMemo(
    () => instantMeiliSearch(meiliHost, meiliSearchKey).searchClient,
    [meiliHost, meiliSearchKey],
  );

  return (
    <div className={`text-neutral-100 ${className}`}>
      <InstantSearch searchClient={searchClient} indexName={indexName}>
        <Configure
          hitsPerPage={hitsPerPage}
          attributesToHighlight={["brand", "model", "ecu_name", "engine_code", "ecu_maker"]}
        />

        <div className="rounded-xl border border-neutral-800 bg-panel p-4">
          <div className="relative flex-1 min-w-[240px]">
            <InstantSearchInputIcon />
            <SearchBox
              placeholder="Suche Marke, Modell, ECU, Motorcode…"
              classNames={{
                root: "w-full",
                form: "relative",
                input:
                  "appearance-none [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden flex h-9 w-full rounded-md border border-neutral-800 bg-panel px-3 py-1 pl-9 text-base shadow-sm transition-colors placeholder:text-neutral-500 focus:outline-none focus:ring-1 focus:ring-red-500 md:text-sm text-neutral-100",
                submitIcon: "hidden",
                resetIcon: "hidden",
                loadingIndicator: "hidden",
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 mt-6">
          <aside className="space-y-5">
            <RefinementFacet
              title="Marke"
              attribute="brand"
              sortBy={["name:asc"]}
              renderIcon={(name) => renderBrandFilterLogo(brandLogos, name)}
            />
            <RefinementFacet
              title="Fahrzeugtyp"
              attribute="vehicle_type"
              labelFor={alientechVehicleTypeLabel}
              renderIcon={renderVehicleTypeIcon}
            />
            <RefinementFacet
              title="ECU-Typ"
              attribute="ecu_type"
              labelFor={ecuTypeLabel}
            />
            <RefinementFacet
              title="ECU-Hersteller"
              attribute="ecu_maker"
              renderIcon={renderEcuBrandFilterLogo}
            />
          </aside>

          <div className="space-y-4 min-w-0">
            <div className="flex items-center justify-between gap-4">
              <div className="text-xs text-neutral-500">
                <Stats
                  classNames={{ root: "inline" }}
                  translations={{
                    rootElementText: ({ nbHits, processingTimeMS }) =>
                      `${nbHits.toLocaleString("de-DE")} Treffer in ${processingTimeMS}ms`,
                  }}
                />
              </div>
              <SortBy
                items={[
                  { value: indexName, label: "Relevanz" },
                  {
                    value: `${indexName}:produced_from_year:desc`,
                    label: "Baujahr (neueste zuerst)",
                  },
                  {
                    value: `${indexName}:produced_from_year:asc`,
                    label: "Baujahr (älteste zuerst)",
                  },
                ]}
                classNames={{
                  root: "shrink-0",
                  select:
                    "h-9 rounded-md border border-neutral-800 bg-panel text-neutral-100 text-sm px-2.5 focus:outline-none focus:ring-2 focus:ring-[#D32222]",
                }}
              />
            </div>

            <InstantSearchResultsLoading>
              <Hits
                classNames={{
                  root: "",
                  list: "divide-y divide-neutral-800/70 border border-neutral-800/70 rounded-xl overflow-hidden bg-panel",
                  item: "",
                }}
                hitComponent={(props) => <Hit {...props} brandLogos={brandLogos} />}
              />
            </InstantSearchResultsLoading>

            <Pagination
              classNames={{
                root: "flex justify-center pt-2",
                list: "flex items-center gap-1",
                item: "px-3 py-1.5 rounded-md text-sm border border-neutral-800 bg-panel text-neutral-300 hover:text-neutral-50 hover:border-neutral-700 transition",
                selectedItem: "!border-red-500/60 !bg-red-500/15 !text-red-400",
                disabledItem: "opacity-40 pointer-events-none",
                link: "block",
              }}
            />
          </div>
        </div>
      </InstantSearch>
    </div>
  );
}

function RefinementFacet({ title, attribute, labelFor, renderIcon, sortBy = ["count:desc"] }) {
  const { items, refine, canToggleShowMore, isShowingMore, toggleShowMore } =
    useRefinementList({
      attribute,
      limit: 8,
      showMore: true,
      showMoreLimit: 40,
      sortBy,
    });

  if (items.length === 0) return null;

  return (
    <div className="rounded-xl border border-neutral-800 bg-panel p-4">
      <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-3">
        {title}
      </div>
      <ul className="space-y-1.5">
        {items.map((item) => (
          <li key={item.label} className="flex items-center">
            <label className="flex items-center gap-2 text-sm text-neutral-300 hover:text-neutral-100 cursor-pointer w-full">
              <input
                type="checkbox"
                checked={item.isRefined}
                onChange={() => refine(item.value)}
                className="accent-red-500 w-3.5 h-3.5 rounded border-neutral-700 bg-panel"
              />
              {renderIcon?.(item.label)}
              <span className="flex-1 truncate">
                {labelFor ? labelFor(item.label) : item.label}
              </span>
              <span className="text-[10px] font-mono text-neutral-500 bg-panel border border-neutral-800 rounded px-1.5 py-0.5">
                {item.count}
              </span>
            </label>
          </li>
        ))}
      </ul>
      {canToggleShowMore && (
        <button
          type="button"
          onClick={toggleShowMore}
          className="mt-2 text-xs text-red-400 hover:text-red-300 transition"
        >
          {isShowingMore ? "Weniger" : "Mehr"}
        </button>
      )}
    </div>
  );
}

function renderEcuBrandFilterLogo(name) {
  return (
    ecuBrandLogoAvailable(name) && (
      <span className="w-7 h-4 flex items-center justify-center shrink-0">
        <EcuBrandLogo name={name} className="max-h-4 max-w-7 w-auto" />
      </span>
    )
  );
}

function renderBrandFilterLogo(brandLogos, name) {
  const logo = brandLogoFor(brandLogos, name);
  return (
    logo && (
      <span className="w-7 h-7 flex items-center justify-center shrink-0">
        <img src={logo} alt="" className="max-h-6 max-w-6 object-contain" loading="lazy" />
      </span>
    )
  );
}

function renderVehicleTypeIcon(type) {
  return <VehicleTypeIcon type={type} className="h-5 w-7" />;
}

const highlightClassNames = {
  highlighted: "bg-red-500/30 text-red-400 rounded-sm px-0.5",
};

function ModeBadge({ mode, info }) {
  const methodLabel = MODE_METHODS[mode];
  const title = `${mode}${info?.locked ? " · gesperrt" : ""}`;
  if (methodLabel) {
    return (
      <span
        title={title}
        className="relative inline-flex items-center justify-center"
      >
        <MethodGlyph label={methodLabel} className="w-8 h-8" />
        {info?.locked && (
          <Lock className="absolute -top-1 -right-1 w-2.5 h-2.5 text-amber-400 bg-panel rounded-full p-0.5" />
        )}
      </span>
    );
  }
  return (
    <span
      title={title}
      className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest border border-neutral-700 text-neutral-300 rounded px-1.5 py-0.5"
    >
      {info?.locked && <Lock className="w-2.5 h-2.5" />}
      {mode}
    </span>
  );
}

function Hit({ hit, brandLogos }) {
  const modes = hit.working_modes || {};
  const logo = brandLogoFor(brandLogos, hit.brand);

  return (
    <div className="group grid grid-cols-1 md:grid-cols-12 gap-4 px-4 py-4 w-full transition">
      {/* Marke */}
      <div className="md:col-span-3 flex items-center gap-3">
        {logo && (
          <img
            src={logo}
            alt={hit.brand}
            className="h-12 w-12 object-contain rounded bg-neutral-900 border border-neutral-800 p-1 shrink-0"
            loading="lazy"
          />
        )}
        <div>
          <div className="font-medium text-neutral-100">
            <Highlight attribute="brand" hit={hit} classNames={highlightClassNames} />
          </div>
          <div className="text-[11px] uppercase tracking-widest text-neutral-500">
            <VehicleTypeIcon type={hit.vehicle_type} className="inline-block h-4 w-5 mr-1 align-[-2px]" />
            {alientechVehicleTypeLabel(hit.vehicle_type)}
          </div>
        </div>
      </div>
      {/* Modell */}
      <div className="md:col-span-2">
        <div className="text-neutral-100">
          {hit.model ? (
            <Highlight attribute="model" hit={hit} classNames={highlightClassNames} />
          ) : (
            "—"
          )}
        </div>
        <div className="text-xs text-neutral-500">
          {[hit.version, [hit.produced_from_year, hit.produced_to_year].filter(Boolean).join("–")]
            .filter(Boolean)
            .join(" · ") || "—"}
        </div>
        <NewBadge firstSeen={hit.first_seen} className="mt-1" />
      </div>
      {/* Motor */}
      <div className="md:col-span-2 text-neutral-200">
        {hit.engine_code ? (
          <Highlight attribute="engine_code" hit={hit} classNames={highlightClassNames} />
        ) : (
          "—"
        )}
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 mt-0.5">
          {[
            hit.fuel ? alientechFuelLabel(hit.fuel) : null,
            hit.engine_power_kw ? `${hit.engine_power_kw} kW` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </div>
      </div>
      {/* ECU */}
      <div className="md:col-span-2">
        <div className="text-neutral-100">
          {hit.ecu_name ? (
            <Highlight attribute="ecu_name" hit={hit} classNames={highlightClassNames} />
          ) : (
            "—"
          )}
        </div>
        <div className="text-[11px] text-neutral-400 mt-0.5">{hit.ecu_maker || ""}</div>
      </div>
      {/* Typ */}
      <div className="md:col-span-1 flex flex-col gap-1 items-start">
        {hit.ecu_type && (
          <span className="inline-flex border border-neutral-700 text-neutral-300 bg-panel text-[10px] font-mono rounded px-2 py-0.5">
            {ecuTypeLabel(hit.ecu_type)}
          </span>
        )}
      </div>
      {/* Verbindungen */}
      <div className="md:col-span-2 flex flex-wrap gap-1.5 items-start">
        {Object.entries(modes).map(([mode, info]) => (
          <ModeBadge key={mode} mode={mode} info={info} />
        ))}
      </div>
    </div>
  );
}
